import { ItemResponse } from '../item-write/item.models';
import { IBatchRow, RowSaveOutcome } from '../item-write/item-batch-save.service';
import { IListingRow } from './item-listing.models';

/**
 * The edited-row state of the listing page (HUBX-8479).
 *
 * Two things, and only two:
 *
 * - **The stored row of every row on the page**, as `GET /items` served it (or as its last
 *   save left it). This is the baseline: what undo restores, what the status rules compare
 *   with, and whose `writableFields` decide what this caller may write.
 * - **ONE `Map<id, ItemResponse>`** holding the full edited state of each row the user
 *   changed. Under a full-state PUT the edited state of a row IS a complete ItemResponse, so
 *   nothing has to record which fields moved.
 *
 * It replaces the parent-held `itemsForm` FormArray, `vendorService.updatedMappedItems` with its
 * partial `itemRequest`, and the hand-synchronisation in `applyChangetoUpdatedItem` /
 * `adjustUpdatedMappedItems`. Row forms are NOT kept here: each surface builds its own on
 * demand from `current(id)` through the shared factory (HUBX-8474), which is what lets the
 * simple row and the expanded view swap without losing an edit.
 *
 * Only `row.item` is ever edited or sent. The envelope beside it (priceDrop, companyName...)
 * is display-only and never travels back through PUT, so the Map holds items, not rows.
 *
 * A plain class, not a service: its lifetime is one listing page, owned by items.component.
 */
export class ItemEditStore {
  private pageRows: IListingRow[] = [];
  private readonly stored = new Map<string, IListingRow>();
  private readonly edits = new Map<string, ItemResponse>();

  /** `onEmptied` is told when the last edit leaves, by whichever path: a save, an undo, an
   *  edit typed back, a refill that catches up with it, a new page. The page's unsaved-only
   *  view closes on it (HUBX-8466) - one rule, kept where every edit comes and goes, rather
   *  than a reset on each path that can empty the Map (the popup's save had none). */
  constructor(private readonly onEmptied?: () => void) { }

  /** Takes a new page and DISCARDS every edit of the previous one. Carrying edits across
   *  pages would leave the user accumulating changes they cannot see and remove the 25-row
   *  bound the fan-out relies on; the caller asks before calling this (needsDiscardConfirmation). */
  load(rows: IListingRow[]): void {
    const before = this.edits.size;
    this.pageRows = [...rows];
    this.stored.clear();
    this.edits.clear();
    for (const row of rows) {
      this.stored.set(row.item.id, row);
    }
    this.settle(before);
  }

  /** The page's rows as stored, in page order. */
  get rows(): readonly IListingRow[] {
    return this.pageRows;
  }

  /** The stored row, envelope included. */
  row(id: string): IListingRow | undefined {
    return this.stored.get(id);
  }

  /** The stored item: the baseline. */
  baseline(id: string): ItemResponse | undefined {
    return this.stored.get(id)?.item;
  }

  /** What the row shows now: its edit if it has one, the stored item otherwise. */
  current(id: string): ItemResponse | undefined {
    return this.edits.get(id) ?? this.baseline(id);
  }

  isEdited(id: string): boolean {
    return this.edits.has(id);
  }

  get editedCount(): number {
    return this.edits.size;
  }

  /** Edited ids in PAGE order, not edit order — the order the grid and the save follow. */
  get editedIds(): string[] {
    return this.pageRows.map(r => r.item.id).filter(id => this.edits.has(id));
  }

  /** Whether leaving the page would lose something. Today pageChanged discards silently;
   *  the discard stays, the silence does not. */
  get needsDiscardConfirmation(): boolean {
    return this.edits.size > 0;
  }

  /** Merge `changes` into what the row shows now. */
  edit(id: string, changes: Partial<ItemResponse>): void {
    this.set(id, { ...this.requireCurrent(id), ...changes });
  }

  /** Replace the row's edited state with `full`. An edit that brings the row back to what
   *  was stored REMOVES the entry, so "edited" always means "differs from the stored item"
   *  and a row the user changed and changed back is not re-sent. */
  set(id: string, full: ItemResponse): void {
    const stored = this.requireBaseline(id);
    const before = this.edits.size;
    if (sameValue(full, stored)) {
      this.edits.delete(id);
    } else {
      // A copy: a caller that keeps mutating its object must not change the edit behind the
      // store's back.
      this.edits.set(id, clone(full));
    }
    this.settle(before);
  }

  /** Restore the row to what the page loaded: a delete from the Map, no HTTP. Replaces
   *  `undoItemUpdates` (item-simple.component.ts), which issued one `getVendorItemById` per
   *  row and could restore something other than what the user was looking at.
   *  Returns whether there was anything to undo. */
  undo(id: string): boolean {
    const before = this.edits.size;
    const undone = this.edits.delete(id);
    this.settle(before);
    return undone;
  }

  undoAll(): void {
    const before = this.edits.size;
    this.edits.clear();
    this.settle(before);
  }

  /** The pending-offers rollback (HUBX-8477): the rows whose offer the user keeps are
   *  reverted through the same path as undo, and so leave the batch. */
  revert(ids: Iterable<string>): void {
    for (const id of ids) {
      this.undo(id);
    }
  }

  /** The edited rows paired with their stored item, in page order — the input of
   *  ItemBatchSaveService.prepare / save. Narrowed to `ids` when given: a row's own Save
   *  sends that row alone (HUBX-8480), and an id that is not edited contributes nothing. */
  batch(ids?: readonly string[]): IBatchRow[] {
    const only = ids ? new Set(ids) : null;
    return this.editedIds
      .filter(id => !only || only.has(id))
      .map(id => ({ baseline: this.requireBaseline(id), edited: this.edits.get(id) as ItemResponse }));
  }

  /**
   * Fold one save outcome back in.
   *
   * - `saved`: the edit becomes the new baseline with the ROTATED token, and leaves the Map.
   *   `itemRowVersion` is null when the master did not move, so the previous one is kept.
   *   The new baseline is provisional in the members the server derives (the three axes,
   *   `effectiveStatus`, `writableFields`): the post-save refill `GET /items?ids=`
   *   (HUBX-8488) replaces it with the stored row.
   * - `failed` / `invalid`: nothing changes. The edit stays, its tokens unrotated, so the
   *   user can correct and retry. How a failure is shown is HUBX-8478.
   *
   * An outcome for a row no longer on the page (the user moved on mid-save) is ignored.
   */
  apply(outcome: RowSaveOutcome): void {
    if (outcome.status !== 'saved') { return; }

    const row = this.stored.get(outcome.id);
    const edited = this.edits.get(outcome.id);
    if (!row || !edited) { return; }

    const item: ItemResponse = {
      ...edited,
      rowVersion: outcome.result.rowVersion,
      itemRowVersion: outcome.result.itemRowVersion ?? edited.itemRowVersion
    };
    const rebased: IListingRow = { ...row, item };
    const before = this.edits.size;
    this.stored.set(outcome.id, rebased);
    this.pageRows = this.pageRows.map(r => r.item.id === outcome.id ? rebased : r);
    this.edits.delete(outcome.id);
    this.settle(before);
  }

  /**
   * Patch rows in place with what the server now holds — the post-save refill
   * (`GET /items?ids=`, HUBX-8488). Each row becomes its new baseline, envelope included, which
   * replaces the provisional baseline `apply` left in the members the server derives.
   *
   * Page order and every other row are untouched, and a row that is not on the page is
   * ignored: the refill never adds rows. The grid is therefore deliberately out of step with
   * the filter after a save — a row the save moved out of the tab stays until the next page
   * load (design page "Listing › After a save").
   *
   * An edit is left alone: a row the user is still changing keeps what the user typed, with
   * its own tokens. If the refilled row now equals that edit, the edit is dropped, so "edited"
   * keeps meaning "differs from the stored item".
   */
  refill(rows: readonly IListingRow[]): void {
    const before = this.edits.size;
    for (const fresh of rows) {
      const id = fresh.item.id;
      if (!this.stored.has(id)) { continue; }
      this.stored.set(id, fresh);
      this.pageRows = this.pageRows.map(r => r.item.id === id ? fresh : r);
      const edit = this.edits.get(id);
      if (edit && sameValue(edit, fresh.item)) {
        this.edits.delete(id);
      }
    }
    this.settle(before);
  }

  /** Tell `onEmptied` when a change took the Map from some edits to none. */
  private settle(before: number): void {
    if (before > 0 && this.edits.size === 0) {
      this.onEmptied?.();
    }
  }

  private requireBaseline(id: string): ItemResponse {
    const item = this.baseline(id);
    if (!item) { throw new Error(`ItemEditStore: ${id} is not on this page.`); }
    return item;
  }

  private requireCurrent(id: string): ItemResponse {
    return this.edits.get(id) ?? this.requireBaseline(id);
  }
}

/** Structural equality over JSON-like values. An `undefined` member counts as absent, the way
 *  it serialises: a form built on demand hands back new arrays and may carry optional
 *  members as undefined, and neither makes a row edited. Array order is significant. */
function sameValue(a: unknown, b: unknown): boolean {
  if (a === b) { return true; }
  if (a === null || b === null || typeof a !== 'object' || typeof b !== 'object') { return false; }
  if (Array.isArray(a) !== Array.isArray(b)) { return false; }
  if (Array.isArray(a)) {
    const other = b as unknown[];
    return a.length === other.length && a.every((v, i) => sameValue(v, other[i]));
  }
  const left = a as Record<string, unknown>;
  const right = b as Record<string, unknown>;
  const keys = new Set([...Object.keys(left), ...Object.keys(right)]);
  for (const key of keys) {
    if (!sameValue(left[key], right[key])) { return false; }
  }
  return true;
}

function clone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}
